import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useMenuPlan } from "@/hooks/useMenuPlan";
import { useNextWeekMenuPlan } from "@/hooks/useNextWeekMenuPlan";
import { useHouseholdProfile } from "@/features/household/useHouseholdProfile";
import {
  EQUIPMENT_OPTIONS,
  WEEKDAY_LABELS_LONG,
} from "@/features/onboarding/constants";
import { resolveAssetKey } from "@/features/cooking/assetResolver";
import {
  ChopIcon,
  StirIcon,
  BoilIcon,
  RoastIcon,
  PeelIcon,
  SeasonIcon,
  RestIcon,
  OvenIcon,
  TimerIcon,
} from "@/components/cooking-icons";
import type { CookingIconName } from "@/components/cooking-icons";
import { AISLE_LABELS, formatWeekRange } from "@/features/menu/menuLabels";
import type { CookingStep } from "@/types/menu";
import type { Aisle } from "@/types/menu";

const ICON_MAP: Record<
  CookingIconName,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  chop: ChopIcon,
  stir: StirIcon,
  boil: BoilIcon,
  roast: RoastIcon,
  peel: PeelIcon,
  season: SeasonIcon,
  rest: RestIcon,
  oven: OvenIcon,
  timer: TimerIcon,
};

function formatTimer(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function BatchOverviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { plan: currentPlan, isLoading } = useMenuPlan();
  const { plan: nextWeekPlan } = useNextWeekMenuPlan();
  const { profile } = useHouseholdProfile();

  // On the user's cooking day, show next week's batch steps for upcoming-week prep
  const cookingDay = profile?.cooking_day ?? 6;
  const cookingDayJs = (cookingDay + 1) % 7;
  const isCookingDay = new Date().getDay() === cookingDayJs;
  const plan = isCookingDay && nextWeekPlan ? nextWeekPlan : currentPlan;
  const isNextWeekMode = isCookingDay && !!nextWeekPlan;

  const steps = plan?.cooking_plan ?? [];

  const recipeTitles = React.useMemo<Record<string, string>>(() => {
    if (!plan) return {};
    const map: Record<string, string> = {};
    for (const day of plan.days) {
      for (const recipe of Object.values(day.meals)) {
        if (recipe) map[recipe.slug] = recipe.title;
      }
    }
    return map;
  }, [plan]);

  const equipmentItems = profile
    ? EQUIPMENT_OPTIONS.filter((e) => profile.equipment.includes(e.id))
    : [];

  const aisleEntries = plan?.shopping_list
    ? (
        Object.entries(plan.shopping_list.items_by_aisle) as [
          Aisle,
          NonNullable<(typeof plan.shopping_list.items_by_aisle)[Aisle]>,
        ][]
      )
        .filter(([, items]) => items && items.length > 0)
        .sort(
          ([a], [b]) =>
            (AISLE_LABELS[a]?.order ?? 99) - (AISLE_LABELS[b]?.order ?? 99),
        )
    : [];

  const showPreparation = equipmentItems.length > 0 || aisleEntries.length > 0;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Sticky header */}
      <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-hair bg-background/95 px-5 py-3.5 backdrop-blur-sm">
        <button
          onClick={() => navigate(-1)}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full hover:bg-bone"
          aria-label="Retour"
        >
          <ArrowLeft className="size-[18px]" strokeWidth={1.5} />
        </button>
        <div className="flex-1 font-serif text-[17px] italic">
          {t("batchOverview.title", {
            day: WEEKDAY_LABELS_LONG[cookingDay].toLowerCase(),
          })}
        </div>
        {!isLoading && steps.length > 0 && (
          <span className="font-mono text-[12px] text-ink-50">
            {steps.length} étapes
          </span>
        )}
      </header>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto pb-28">
        {/* Sunday next-week mode banner */}
        {isNextWeekMode && (
          <div className="border-b border-accent/30 bg-accent/5 px-6 py-3">
            <p className="text-[12px] text-accent">
              Vous préparez la semaine prochaine — achetez les courses
              aujourd'hui.
            </p>
          </div>
        )}

        {/* Sunday with no next-week menu — prompt generation */}
        {isCookingDay && !nextWeekPlan && !currentPlan && !isLoading && (
          <section className="px-6 py-12 text-center">
            <p className="mb-2 font-serif text-[18px] italic">
              Pas encore de menu pour la semaine prochaine
            </p>
            <p className="mb-5 text-[13px] text-ink-50">
              Générez votre menu avant de faire les courses du dimanche.
            </p>
            <Link
              to="/menu/next?generate=true"
              className="inline-flex items-center gap-2 bg-accent px-4 py-2.5 text-[13px] text-background"
            >
              <Sparkles className="size-4" strokeWidth={1.5} />
              Générer le menu de la semaine prochaine
            </Link>
          </section>
        )}

        {/* Sunday with current week batch but no next-week plan — secondary nudge */}
        {isCookingDay && !nextWeekPlan && !!currentPlan && !isLoading && (
          <div className="border-b border-hair px-6 py-3">
            <Link
              to="/menu/next?generate=true"
              className="flex items-center justify-between text-[12px]"
            >
              <span className="text-ink-60">
                <Sparkles
                  className="mr-1.5 inline size-3.5 text-accent"
                  strokeWidth={1.5}
                />
                Générer le menu de la semaine prochaine
              </span>
              <span className="text-accent">→</span>
            </Link>
          </div>
        )}

        {/* Week banner */}
        {plan && (plan.theme || plan.week_start) && (
          <section className="border-b border-hair px-6 py-5">
            {plan.theme && (
              <p className="mb-1 text-[11px] uppercase tracking-[0.18em] text-accent">
                {plan.theme}
              </p>
            )}
            {plan.week_start && (
              <p className="font-serif text-[15px] text-ink-50">
                {formatWeekRange(plan.week_start, plan.days.length)}
              </p>
            )}
          </section>
        )}

        {/* Preparation section */}
        {(isLoading || showPreparation) && (
          <section className="px-6 pb-6 pt-5">
            <h2 className="mb-4 text-[11px] uppercase tracking-[0.18em] text-ink-50">
              {t("batchOverview.preparation")}
            </h2>

            {isLoading ? (
              <div className="space-y-3">
                <div className="h-24 w-full bg-bone" />
                <div className="h-32 w-full bg-bone" />
              </div>
            ) : (
              <>
                {/* Equipment card */}
                {equipmentItems.length > 0 && (
                  <div className="mb-3 border border-hair bg-paper px-5 py-4">
                    <p className="mb-3 text-[13px] font-medium">
                      {t("batchOverview.equipment")}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {equipmentItems.map((e) => (
                        <span
                          key={e.id}
                          className="rounded-full border border-hair bg-background px-3 py-1 text-[12px] text-ink-70"
                        >
                          {e.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Ingredients card */}
                {aisleEntries.length > 0 && (
                  <div className="border border-hair bg-paper px-5 py-4">
                    <p className="mb-3 text-[13px] font-medium">
                      {t("batchOverview.ingredients")}
                    </p>
                    <ul className="space-y-2.5">
                      {aisleEntries.map(([aisle, items]) => (
                        <li
                          key={aisle}
                          className="flex items-center justify-between text-[13px]"
                        >
                          <span className="text-foreground">
                            {AISLE_LABELS[aisle]?.emoji}{" "}
                            {AISLE_LABELS[aisle]?.fr ?? aisle}
                          </span>
                          <span className="font-mono text-[11px] text-ink-50">
                            {items.length}{" "}
                            {items.length === 1 ? "article" : "articles"}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {/* Steps section */}
        <section>
          <div className="flex items-center gap-2 px-6 pb-4 pt-5">
            <h2 className="text-[11px] uppercase tracking-[0.18em] text-ink-50">
              {t("batchOverview.stepsTitle")}
            </h2>
            {!isLoading && steps.length > 0 && (
              <span className="text-[11px] text-ink-30">· {steps.length}</span>
            )}
          </div>

          {isLoading ? (
            <StepsSkeleton />
          ) : steps.length === 0 ? (
            <p className="px-6 text-[14px] text-ink-50">
              {t("batchOverview.noSteps")}
            </p>
          ) : (
            <ul className="border-t border-hair">
              {steps.map((step) => (
                <StepRow
                  key={step.order}
                  step={step}
                  recipeTitles={recipeTitles}
                />
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Sticky footer CTA */}
      <div className="fixed bottom-0 left-0 right-0 border-t border-hair bg-background/95 p-5 backdrop-blur-sm">
        <button
          onClick={() => navigate("/cooking")}
          disabled={steps.length === 0}
          className="flex w-full items-center justify-center bg-foreground py-4 font-serif text-[17px] italic text-background transition-opacity active:opacity-80 disabled:opacity-40"
        >
          {t("batchOverview.startCta")}
        </button>
      </div>
    </div>
  );
}

function StepRow({
  step,
  recipeTitles,
}: {
  step: CookingStep;
  recipeTitles: Record<string, string>;
}) {
  const { t } = useTranslation();
  const iconName = resolveAssetKey(step.asset_key);
  const Icon = iconName ? ICON_MAP[iconName] : null;

  const recipeNames =
    step.recipe_slugs && step.recipe_slugs.length >= 2
      ? step.recipe_slugs.map((slug) => recipeTitles[slug] ?? slug).join(", ")
      : null;

  return (
    <li className="flex gap-3 border-b border-hair px-6 py-3.5 last:border-b-0">
      {/* Order badge */}
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent/10 font-mono text-[11px] text-accent">
        {step.order}
      </span>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2">
          {Icon && (
            <Icon
              className="mt-0.5 size-4 shrink-0 text-foreground/50"
              aria-hidden
            />
          )}
          <div className="min-w-0 flex-1 font-serif text-[15px] leading-snug">
            <span className="font-semibold">{step.verb}</span>
            <span> {step.target}</span>
            {step.quantity && (
              <span className="text-ink-50"> — {step.quantity}</span>
            )}
          </div>
        </div>

        {step.detail && (
          <p className="mt-1 text-[12px] leading-snug text-ink-50">
            {step.detail}
          </p>
        )}

        {recipeNames && (
          <p className="mt-0.5 text-[11px] text-ink-30">
            {t("batchOverview.stepForRecipes")} {recipeNames}
          </p>
        )}

        {step.timer_seconds != null && (
          <div className="mt-1 font-mono text-[11px] text-accent">
            ⏱ {formatTimer(step.timer_seconds)}
          </div>
        )}

        {step.parallel_with.length > 0 && (
          <div className="mt-1 text-[11px] text-ink-30">
            Avec étape{step.parallel_with.length > 1 ? "s" : ""}{" "}
            {step.parallel_with.join(", ")}
          </div>
        )}
      </div>
    </li>
  );
}

function StepsSkeleton() {
  return (
    <ul className="border-t border-hair">
      {Array.from({ length: 5 }).map((_, i) => (
        <li key={i} className="flex gap-3 border-b border-hair px-6 py-3.5">
          <div className="h-6 w-6 shrink-0 rounded-full bg-bone" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-3/4 bg-bone" />
            <div className="h-3 w-1/2 bg-bone" />
          </div>
        </li>
      ))}
    </ul>
  );
}
